import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface AnalysisData {
  id: string;
  status: string;
  session_result?: any;
  result?: any;
  progress?: number;
  message?: string;
  created_at?: string;
  filename?: string;
  [key: string]: any;
}

interface AnalysisCacheContextType {
  getCachedAnalysis: (analysisId: string) => AnalysisData | null;
  setCachedAnalysis: (analysisId: string, data: AnalysisData) => void;
  clearCache: () => void;
  getCacheSize: () => number;
  isCached: (analysisId: string) => boolean;
}

interface AnalysisCacheProviderProps {
  children: ReactNode;
  maxCacheSize?: number;
}

const AnalysisCacheContext = createContext<AnalysisCacheContextType | undefined>(undefined);

export const AnalysisCacheProvider: React.FC<AnalysisCacheProviderProps> = ({ 
  children, 
  maxCacheSize = 20 
}) => {
  const [cache, setCache] = useState<Map<string, AnalysisData>>(() => new Map());

  const getCachedAnalysis = useCallback((analysisId: string): AnalysisData | null => {
    return cache.get(analysisId) || null;
  }, [cache]);

  const setCachedAnalysis = useCallback((analysisId: string, data: AnalysisData) => {
    // Only cache completed analyses to avoid stale processing states
    if (data.status !== 'completed') return;

    setCache(prevCache => {
      const newCache = new Map(prevCache);

      // If we're at max capacity and this is a new entry, remove oldest
      if (newCache.size >= maxCacheSize && !newCache.has(analysisId)) {
        const firstKey = newCache.keys().next().value;
        if (firstKey) {
          newCache.delete(firstKey);
        }
      }

      newCache.set(analysisId, data);
      return newCache;
    });
  }, [maxCacheSize]);

  const clearCache = useCallback(() => {
    setCache(new Map());
  }, []);

  const getCacheSize = useCallback(() => {
    return cache.size;
  }, [cache]);

  const isCached = useCallback((analysisId: string): boolean => {
    return cache.has(analysisId);
  }, [cache]);

  const value: AnalysisCacheContextType = useMemo(() => ({
    getCachedAnalysis,
    setCachedAnalysis,
    clearCache,
    getCacheSize,
    isCached
  }), [getCachedAnalysis, setCachedAnalysis, clearCache, getCacheSize, isCached]);

  return (
    <AnalysisCacheContext.Provider value={value}>
      {children}
    </AnalysisCacheContext.Provider>
  );
};

export const useAnalysisCache = (): AnalysisCacheContextType => {
  const context = useContext(AnalysisCacheContext);
  if (context === undefined) {
    throw new Error('useAnalysisCache must be used within an AnalysisCacheProvider');
  }
  return context;
};
